import React, { useState, useEffect, useCallback } from "react";
import { useRatingActions } from "../../hooks/useCallRating";
import { useRatingSubmission, useDebouncedRatingSubmission, useIssuesUpdate } from "./hooks";
import { ThankYouMessage } from "./ThankYouMessage";
import { FeedbackForm } from "./FeedbackForm";
import type { FeedbackModalProps } from "./types";

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ conversationId, onComplete }) => {
  const [rating, setRating] = useState<number>(0);
  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
  const [otherFeedback, setOtherFeedback] = useState<string>("");
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const { options, updateRating } = useRatingActions();
  const { submitRatingWithPayload, isSubmitting, hasSubmitted } =
    useRatingSubmission(conversationId);
  const debouncedSubmit = useDebouncedRatingSubmission(conversationId, submitRatingWithPayload);
  const debouncedUpdateIssues = useIssuesUpdate(conversationId);

  const handleRatingChange = useCallback(
    async (value: number) => {
      if (value === 0) return;
      setRating(value);

      if (value === 5) {
        await submitRatingWithPayload(value);
      } else {
        debouncedSubmit(value, selectedIssues, otherFeedback);
      }
    },
    [submitRatingWithPayload, debouncedSubmit, selectedIssues, otherFeedback]
  );

  const handleIssueToggle = useCallback(
    (issueId: string) => {
      setSelectedIssues((prev) => {
        const newIssues = prev.includes(issueId)
          ? prev.filter((i) => i !== issueId)
          : [...prev, issueId];

        if (hasSubmitted && newIssues.length > 0) {
          debouncedUpdateIssues(newIssues);
        }

        return newIssues;
      });
    },
    [hasSubmitted, debouncedUpdateIssues]
  );

  const handleFinalSubmit = useCallback(async () => {
    try {
      await updateRating(conversationId, {
        issues: selectedIssues,
        feedback: otherFeedback,
      });
      setFeedbackSubmitted(true);
      setTimeout(() => onComplete(), 2000);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      onComplete();
    }
  }, [conversationId, selectedIssues, otherFeedback, updateRating, onComplete]);

  useEffect(() => {
    if (rating === 5 && hasSubmitted && !isSubmitting) {
      setFeedbackSubmitted(true);
      const timer = setTimeout(() => onComplete(), 2000);
      return () => clearTimeout(timer);
    }
  }, [rating, hasSubmitted, isSubmitting, onComplete]);

  const showThankYou = feedbackSubmitted || (rating === 5 && hasSubmitted && !isSubmitting);

  return (
    <div className="flex flex-col items-center justify-center w-full text-[#10333B] min-h-[200px]">
      {showThankYou ? (
        <ThankYouMessage />
      ) : (
        <FeedbackForm
          rating={rating}
          hoveredRating={hoveredRating}
          selectedIssues={selectedIssues}
          otherFeedback={otherFeedback}
          options={options}
          isSubmitting={isSubmitting}
          onRatingChange={handleRatingChange}
          onHoverRating={setHoveredRating}
          onHoverLeave={() => setHoveredRating(0)}
          onIssueToggle={handleIssueToggle}
          onFeedbackChange={setOtherFeedback}
          onFinalSubmit={handleFinalSubmit}
        />
      )}
    </div>
  );
};

// Export the wrapper for use in portals
export { FeedbackModalWrapper } from './FeedbackModalWrapper';
